import { AdminSettingsFormWrapper } from 'components/shared/AdminSettings/AdminSettingsFormWrapper';
import * as yup from 'yup';
import { FormContent } from 'components/admin-portal/Settings/Match/components/FormContent';
import { useGetCostCenterSettings } from 'queries';
import { AdminSettingsResponse } from 'components/types';
import {
  transformAdminSettingsToFormValues,
  transformAdminSettingsToRequestValues
} from 'components/utils';
import { useSaveResource } from 'RestAPIs/useSaveResource';
import { COSTCENTER_SETTINGS_GROUP } from 'RestAPIs/endpoints';
import { MATCH_SETTINGS_GROUP } from 'components/admin-portal/Settings/Match/MatchSettings';
import { DTR_SETTINGS_GROUP, TRAVELCOST_SETTINGS_GROUP } from '../Settings';
import { useEffect, useState } from 'react';
import useGetRosterStateSettings from '../hooks/useGetRosterStateSettings';
import useSaveRosterStateSettings from '../hooks/useSaveRosterStateSettings';
import PlanningCycle from './PlanningCycle';

const CostCenterSettings = ({ costCenterId, settingsGroup }) => {
  const { data: rosterStateSettings, refetch: refetchRosterStateSettings } =
    useGetRosterStateSettings(costCenterId);
  const { mutate: saveRosterSettings } = useSaveRosterStateSettings();

  // Initialize state for the checkboxes
  const [isPublishedEnabled, setIsPublishedEnabled] = useState(false);
  const [isVisibleEnabled, setIsVisibleEnabled] = useState(false);

  const [openPlanningCycleDialog, setOpenPlanningCycleDialog] = useState({
    published: false,
    visible: false
  });

  // Set the state once the data is fetched from the API
  useEffect(() => {
    if (rosterStateSettings) {
      const publishedSetting = rosterStateSettings.find(
        (setting) => setting.rosterState === 'PUBLISHED'
      );
      const visibleSetting = rosterStateSettings.find(
        (setting) => setting.rosterState === 'VISIBLE'
      );

      setIsPublishedEnabled(publishedSetting?.enabled || false);
      setIsVisibleEnabled(visibleSetting?.enabled || false);
    }
  }, [rosterStateSettings]);

  // Handle opening the dialogs for planning cycles
  const handleDialogOpen = (type: 'PUBLISHED' | 'VISIBLE') => {
    setOpenPlanningCycleDialog((prevState) => ({
      ...prevState,
      [type.toLowerCase()]: true
    }));
  };

  const handleDialogClose = (
    type: 'PUBLISHED' | 'VISIBLE',
    wasSaved: boolean
  ) => {
    setOpenPlanningCycleDialog((prevState) => ({
      ...prevState,
      [type.toLowerCase()]: false
    }));

    if (wasSaved) {
      if (type === 'PUBLISHED') setIsPublishedEnabled(true);
      if (type === 'VISIBLE') setIsVisibleEnabled(true);

      refetchRosterStateSettings();
    }
  };

  // Save roster state when checkbox is unchecked
  const handleCheckboxChange = (
    type: 'PUBLISHED' | 'VISIBLE',
    checked: boolean
  ) => {
    if (!checked) {
      const rosterStatePayload = {
        rosterState: type,
        enabled: false,
        recurrenceRule: null,
        cycleWindow: null,
        rounds: []
      };
      saveRosterSettings(
        { costCenterId, planningCyclePayload: rosterStatePayload },
        {
          onSuccess: () => {
            if (type === 'PUBLISHED') setIsPublishedEnabled(false);
            if (type === 'VISIBLE') setIsVisibleEnabled(false);
          }
        }
      );
    } else {
      handleDialogOpen(type);
    }
  };

  if (settingsGroup === 'roster-state-settings') {
    const publishedCycle = rosterStateSettings?.find(
      (setting) => setting.rosterState === 'PUBLISHED'
    );
    const visibleCycle = rosterStateSettings?.find(
      (setting) => setting.rosterState === 'VISIBLE'
    );

    return (
      <PlanningCycle
        isPublishedEnabled={isPublishedEnabled}
        setIsPublishedEnabled={setIsPublishedEnabled}
        isVisibleEnabled={isVisibleEnabled}
        setIsVisibleEnabled={setIsVisibleEnabled}
        handleDialogOpen={handleDialogOpen}
        handleCheckboxChange={handleCheckboxChange}
        handleDialogClose={handleDialogClose}
        openPlanningCycleDialog={openPlanningCycleDialog}
        costCenterId={costCenterId}
        publishedCycle={publishedCycle}
        visibleCycle={visibleCycle}
      />
    );
  }

  const { data: settings = [], isFetching } =
    useGetCostCenterSettings<AdminSettingsResponse>(
      settingsGroup,
      costCenterId
    );

  const getSchema = (group: string) => {
    if (group === MATCH_SETTINGS_GROUP) {
      return yup.object({
        self_match_flex_match_connect: yup.boolean(),
        self_match: yup.string(),
        self_match_roster_match_connect: yup.boolean(),
        self_match_flex_deployableasflex_action: yup.string(),
        self_match_automatic_publish_preference2_action: yup.string(),
        self_match_automatic_visible_preference1_action: yup.string(),
        self_match_automatic_publish_own_action: yup.string(),
        self_match_automatic_publish_shared_action: yup.string(),
        self_match_automatic_visible_own_action: yup.string(),
        self_match_automatic_visible_shared_action: yup.string(),
        self_match_automatic_publish_preference1_action: yup.string(),
        self_match_automatic_visible_preference2_action: yup.string(),
        self_match_ignore_negative_balance: yup.boolean(),
        self_match_flex_preference1_action: yup.string(),
        self_match_flex_deployableasflexpreference1_action: yup.string(),
        self_match_flex_flex_action: yup.string()
      });
    }
    if (group === DTR_SETTINGS_GROUP) {
      return yup.object({
        dtr_calculation_enabled: yup.boolean()
      });
    }
    if (group === TRAVELCOST_SETTINGS_GROUP) {
      return yup.object({
        'exclude-travelcost-calculation': yup.boolean()
      });
    }
    return yup.object().shape({}); // default empty schema
  };

  const { mutate: saveSetting } = useSaveResource({
    url: COSTCENTER_SETTINGS_GROUP,
    id: settings[0]?.groupId
  });

  const onSubmit = async (formData) => {
    // 1. Handle "self-match"
    if (settingsGroup === MATCH_SETTINGS_GROUP) {
      const matchSettings = settings[0]?.settings;

      saveSetting({
        ...settings[0],
        settings: transformAdminSettingsToRequestValues(
          formData,
          matchSettings
        ),
        active: settings[0].active
      });
    }

    // 2. Handle "exclude-travelcost-calculation"
    if (settingsGroup === TRAVELCOST_SETTINGS_GROUP) {
      const excludeTravelCostSettings = settings[0]?.settings.filter(
        (setting) => setting.key === 'exclude-travelcost-calculation'
      );

      const newExcludeTravelCostSetting = {
        key: 'exclude-travelcost-calculation',
        value: formData['exclude-travelcost-calculation'] // Get the value from the form data
      };

      const transformedExcludeTravelCostSettings =
        excludeTravelCostSettings?.length
          ? transformAdminSettingsToRequestValues(
              formData,
              excludeTravelCostSettings
            )
          : [newExcludeTravelCostSetting]; // Create new if none exist

      saveSetting({
        ...settings[0],
        settings: transformedExcludeTravelCostSettings,
        groupName: settingsGroup,
        active: settings[0]?.active || true
      });
    }

    // 3. Handle "dtr.calculation.enabled"
    if (settingsGroup === DTR_SETTINGS_GROUP) {
      const dtrCalculationSettings = settings[0]?.settings.filter(
        (setting) => setting.key === 'dtr.calculation.enabled'
      );

      const newDtrCalculationSetting = {
        key: 'dtr.calculation.enabled',
        value: formData['dtr_calculation_enabled']
      };

      const transformedDtrCalculationSettings = dtrCalculationSettings?.length
        ? transformAdminSettingsToRequestValues(
            formData,
            dtrCalculationSettings
          )
        : [newDtrCalculationSetting];

      saveSetting({
        ...settings[0],
        settings: transformedDtrCalculationSettings,
        groupName: settingsGroup,
        active: settings[0]?.active || true
      });
    }
  };

  return (
    <>
      <AdminSettingsFormWrapper
        submitForm={onSubmit}
        schema={getSchema(settingsGroup)}
        fixedHeightForm={false}
        isLoading={false}
        data={transformAdminSettingsToFormValues(settings[0])}
        isFetching={isFetching}
      >
        <FormContent settingsGroup={settingsGroup} />
      </AdminSettingsFormWrapper>
    </>
  );
};

export default CostCenterSettings;
